import { useMemo } from 'react'
import { MdOutlineDeleteOutline } from 'react-icons/md'

import { createCrudResourceContext } from '@/Core/UI/CrudResource/CrudResourceContextProvider'
import { ResourceList } from '@/Core/UI/CrudResource/ResourceList'
import { CrudResourceConfig } from '@/Ship/Configs/CrudResourceConfig'

import { TaskResource } from '../Resources/TaskResource'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'

/**
 * «Видалені» — prefilter `{ isRemoved: true }` → `filters={"and":[{isRemoved = true}]}`,
 * що вимикає `RemovedExtension` і показує м'яко видалені завдання (SPEC-008 §4.4).
 *
 * Read-only забезпечує бекенд: на видаленому item `_permissions.update/delete = false`
 * (SPEC-005 §3.2 — перевірено), тож Ship-`accessFn` ховає редагування/видалення, а
 * картка відкривається read-only (бек дозволяє `TASK_READ` на видаленому).
 *
 * Хвіст 05.3 — прибрати кнопку «Створити» на цій сторінці. Рев'ю 05 попереджало, що
 * власний page-level accessFn ПЕРЕКРИВАЄ глобальний Ship-`accessFn` і тим ламає
 * `read_collection`. Тому не хардкодимо відповіді, а ДЕЛЕГУЄМО все глобальному
 * `CrudResourceConfig.accessFn` (read_collection/read/update/delete лишаються як були),
 * і лише для `create` повертаємо `false` — кнопка «Додати» (`headerPage/AddButton`
 * гейтить видимість саме на `accessFn({accessMode:'create'})`) зникає, список цілий.
 */
export default function RemovedTasksPage() {
    const listContext = useMemo(
        () =>
            createCrudResourceContext({
                contextType: 'list',
                registryKey: 'Task.Removed.list',
                resource: TaskResource,
                prefilter: { isRemoved: true },
                accessFn: (params: { accessMode?: string }) =>
                    params?.accessMode === 'create' ? false : CrudResourceConfig.accessFn(params),
                // Каст на межі нетипізованої .jsx-фабрики: її inferred-параметр не містить
                // resource/prefilter/accessFn (їх фабрика читає через ...props у рантаймі).
                // Приводимо до її ж параметра — без `any` (пор. Core `UniversalResourcePage.tsx:70`).
            } as unknown as Parameters<typeof createCrudResourceContext>[0]),
        []
    )

    // `registryKey` обов'язковий у типі `ResourceList` ЦІЛІ — лишаємо свідомо (§хвіст 05.4).
    return <ResourceList crudContext={listContext} registryKey="Task.Removed.list" />
}

export const pageConfig: IPageConfig = {
    key: 'Task.Removed',
    title: 'Видалені',
    icon: <MdOutlineDeleteOutline />,
}
